import { useQuery } from '@tanstack/react-query'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { api } from '@/lib/api'

/**
 * CVE detail card surfacing ransomware groups whose leak-site claims
 * reference this CVE in the trailing 90-day window (Plan B Plan 2 T18).
 *
 * Spec §5.5: render only when the list is non-empty — empty result hides
 * the section entirely (no zero-state widget on detail surfaces).
 *
 * Wraps `GET /api/leak-sites/by-cve/:cveId?days=90` (Plan B Plan 2 T8).
 */

interface CveCrossRef {
  groupId: string
  groupName: string
  recentClaimCount: number
  lastClaimAt: string
}

interface CveCrossRefResponse {
  groups: CveCrossRef[]
}

const DEFAULT_DAYS = 90

export interface LeakSiteGroupsForCveCardProps {
  cveId: string
  days?: number
}

export function LeakSiteGroupsForCveCard({
  cveId,
  days = DEFAULT_DAYS,
}: LeakSiteGroupsForCveCardProps) {
  const { data } = useQuery({
    queryKey: ['leak-sites', 'by-cve', cveId, days],
    queryFn: () =>
      api.get<CveCrossRefResponse>(
        `/leak-sites/by-cve/${encodeURIComponent(cveId)}?days=${days}`,
      ),
    enabled: Boolean(cveId),
    staleTime: 60_000,
  })

  if (!data || data.groups.length === 0) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Ransomware groups referencing this CVE ({days}d)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2" aria-label="Ransomware groups referencing this CVE">
          {data.groups.map((g) => {
            const word = g.recentClaimCount === 1 ? 'claim' : 'claims'
            return (
              <li
                key={g.groupId}
                className="flex items-center justify-between border-b border-border/40 pb-2 last:border-b-0 last:pb-0"
              >
                <span className="font-medium">{g.groupName}</span>
                <span className="text-sm text-muted-foreground tabular-nums">
                  {g.recentClaimCount} {word}
                </span>
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}
